import { createBrowserRouter } from "react-router";

import { ActivityLogPage } from "ActivityLog/Pages/ActivityLogPage";
import { ActorDetailPage } from "Actors/Pages/ActorDetailPage";
import { ActorsPage } from "Actors/Pages/ActorsPage";
import { RegisterActorPage } from "Auth/Pages/RegisterActorPage";
import { RegisterPartnerPage } from "Auth/Pages/RegisterPartnerPage";
import { ResetPasswordPage } from "Auth/Pages/ResetPasswordPage";
import { SetPasswordPage } from "Auth/Pages/SetPasswordPage";
import { SignInPage } from "Auth/Pages/SignInPage";
import { BillingPage } from "Billing/Pages/BillingPage";
import { ClientDetailPage } from "Clients/Pages/ClientDetailPage";
import { ClientsPage } from "Clients/Pages/ClientsPage";
import { DashboardPage } from "Dashboard/Pages/DashboardPage";
import { AccessRoleCode } from "Infrastructure/Api/Api";
import { Error403Page } from "Infrastructure/Pages/Error403Page";
import { Error404Page } from "Infrastructure/Pages/Error404Page";
import { AuthenticatedRoute } from "Infrastructure/Routing/AuthenticatedRoute";
import { UnauthenticatedRoute } from "Infrastructure/Routing/UnauthenticatedRoute";
import { AppRouting, getRoute } from "Infrastructure/Utils/UrlUtils";
import { InvoicesPage } from "Invoices/Pages/InvoicesPage";
import { PartnerInvoicesPage } from "Invoices/Pages/PartnerInvoicesPage";
import { PrivacyPolicyPage } from "Legal/Pages/PrivacyPolicyPage";
import { TermsOfServicePage } from "Legal/Pages/TermsOfServicePage";
import { ModuleDetailPage } from "Modules/Pages/ModuleDetailPage";
import { ModulesPage } from "Modules/Pages/ModulesPage";
import { MusicAudioPage } from "MusicAudio/Pages/MusicAudioPage";
import { PartnerDetailPage } from "Partners/Pages/PartnerDetailPage";
import { PartnersPage } from "Partners/Pages/PartnersPage";
import { PriceCategoriesPage } from "PriceCategories/Pages/PriceCategoriesPage";
import { PriceCategoryDetailPage } from "PriceCategories/Pages/PriceCategoryDetailPage";
import { ProjectDetailPage } from "Projects/Pages/ProjectDetailPage";
import { ProjectJobDetailPage } from "Projects/Pages/ProjectJobDetailPage";
import { ProjectsPage } from "Projects/Pages/ProjectsPage";
import { RegistrationRequestDetailPage } from "RegistrationRequests/Pages/RegistrationRequestDetailPage";
import { RegistrationRequestsPage } from "RegistrationRequests/Pages/RegistrationRequestsPage";
import { ReportingPage } from "Reporting/Pages/ReportingPage";
import { HelpPage } from "Support/Pages/HelpPage";
import { UsersPage } from "Users/Pages/UsersPage";
import { WithdrawalApprovalDetailPage } from "WithdrawalApprovals/Pages/WithdrawalApprovalDetailPage";
import { WithdrawalApprovalsPage } from "WithdrawalApprovals/Pages/WithdrawalApprovalsPage";

const PARTNER_ROLES = [AccessRoleCode.Partner, AccessRoleCode.Actor];
const ADMIN_ROLES = [AccessRoleCode.SysAdmin];

const appRoutes = [
  {
    path: getRoute(AppRouting.SignIn),
    element: (
      <UnauthenticatedRoute>
        <SignInPage />
      </UnauthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.RegisterPartner),
    element: (
      <UnauthenticatedRoute>
        <RegisterPartnerPage />
      </UnauthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.RegisterActor),
    element: (
      <UnauthenticatedRoute>
        <RegisterActorPage />
      </UnauthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.ResetPassword),
    element: (
      <UnauthenticatedRoute>
        <ResetPasswordPage />
      </UnauthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.SetPassword),
    element: <SetPasswordPage />,
  },
  {
    path: getRoute(AppRouting.PrivacyPolicy),
    element: <PrivacyPolicyPage />,
  },
  {
    path: getRoute(AppRouting.TermsOfService),
    element: <TermsOfServicePage />,
  },
  {
    path: getRoute(AppRouting.Help),
    element: <HelpPage />,
  },
  {
    path: getRoute(AppRouting.Dashboard),
    element: (
      <AuthenticatedRoute>
        <DashboardPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.Projects),
    element: (
      <AuthenticatedRoute>
        <ProjectsPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.ProjectJobDetail),
    element: (
      <AuthenticatedRoute>
        <ProjectJobDetailPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.ProjectDetail),
    element: (
      <AuthenticatedRoute>
        <ProjectDetailPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.MusicAudio),
    element: (
      <AuthenticatedRoute>
        <MusicAudioPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.Billing),
    element: (
      <AuthenticatedRoute allowedRoles={PARTNER_ROLES}>
        <BillingPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.Clients),
    element: (
      <AuthenticatedRoute>
        <ClientsPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.ClientDetail),
    element: (
      <AuthenticatedRoute>
        <ClientDetailPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.Actors),
    element: (
      <AuthenticatedRoute>
        <ActorsPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.ActorDetail),
    element: (
      <AuthenticatedRoute>
        <ActorDetailPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.Partners),
    element: (
      <AuthenticatedRoute>
        <PartnersPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.PartnerDetail),
    element: (
      <AuthenticatedRoute>
        <PartnerDetailPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.Modules),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <ModulesPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.ModuleDetail),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <ModuleDetailPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.PriceCategories),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <PriceCategoriesPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.PriceCategoryDetail),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <PriceCategoryDetailPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.Users),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <UsersPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.WithdrawalApprovals),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <WithdrawalApprovalsPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.WithdrawalApprovalDetail),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <WithdrawalApprovalDetailPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.Invoices),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <InvoicesPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.ActivityLog),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <ActivityLogPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.Reporting),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <ReportingPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.RegistrationRequests),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <RegistrationRequestsPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.RegistrationRequestDetail),
    element: (
      <AuthenticatedRoute allowedRoles={ADMIN_ROLES}>
        <RegistrationRequestDetailPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.PartnerInvoices),
    element: (
      <AuthenticatedRoute>
        <PartnerInvoicesPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: getRoute(AppRouting.Error403),
    element: <Error403Page />,
  },
  {
    path: "*",
    element: <Error404Page />,
  },
];

export const appRouter = createBrowserRouter(appRoutes, {
  basename: import.meta.env.VITE_BASE_URL,
});
